import { format, formatDate } from "date-fns";
import { enUS } from "date-fns/locale";
import {
  TCartPass,
  TGlobalStore,
  TGroupDetails,
  TPass,
  TPassOnAccount,
  TUserData,
} from "src/shared/types/globalStotre.types";

/**
 * Extract url params into javascript object
 * @param url string
 * @returns javascript object containing the params and value
 */
export function extractUrlParams(url: string) {
  let result = Object.create(null);
  let paramsStr = url.split("?")[1];
  if (!paramsStr) return result;

  const paramsArray = paramsStr.split("&");

  paramsArray.forEach((param) => {
    const [key, value] = param.split("=");
    result[key] = decodeURIComponent(value);
  });
  return result;
}

export function throttleTime(delay: number, callback?: () => void) {
  return new Promise<void>((resolve, reject) => {
    let timeout = setTimeout(() => {
      callback?.();
      resolve();
      clearTimeout(timeout);
    }, delay);
  });
}

export function pluralize(noun: string, count: number) {
  if (count <= 1) {
    return noun;
  } else {
    // Check for special cases where "es" is needed
    if (
      noun.endsWith("s") ||
      noun.endsWith("sh") ||
      noun.endsWith("ch") ||
      noun.endsWith("x") ||
      noun.endsWith("z")
    ) {
      return noun + "es";
    } else {
      return noun + "s";
    }
  }
}

export function getMonthName(dateStr: string) {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const date = new Date(dateStr);
  return months[date.getMonth()];
}

export const convertISODate = (date: string) => {
  if (date === "TBA") {
    return date;
  }
  return format(new Date(date), "MM/dd/yy 'at' hh:mm a", {
    locale: enUS,
  });
};

export const getUSCurrency = (number: number) =>
  number.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

export function dispatchCustomEvent<T = any>(eventName: string, detail?: T) {
  const event = new CustomEvent(eventName, { detail });
  window.dispatchEvent(event);
}

export function calcOptOutDate(cfgDate: Date | null | undefined) {
  let today = new Date();
  if (cfgDate && today < cfgDate) today = cfgDate;
  const optInDate = 15;
  const date = today.getDate();
  if (date <= optInDate) {
    return new Date(today.getFullYear(), today.getMonth(), optInDate);
  } else {
    return new Date(today.getFullYear(), today.getMonth() + 1, optInDate);
  }
}

export function formatSSDate(date: string, format: string) {
  let formattedDate: string = "";
  try {
    const dateStrFormatted = date?.replaceAll("-", "/");
    const jsDate = new Date(dateStrFormatted);
    if (isNaN(jsDate.getTime())) formattedDate = "TBA";
    formattedDate = formatDate(new Date(dateStrFormatted), format);
  } catch (error) {
    formattedDate = date;
    console.log("error formatting date", error);
  } finally {
    return formattedDate;
  }
}

export function formatSqlDate(date: string, format: string) {
  const dt = new Date(date);
  // Output the date in UTC format to avoid any timezone conversion
  const utcDate = new Date(
    dt.getUTCFullYear(),
    dt.getUTCMonth(),
    dt.getUTCDate(),
    dt.getUTCHours(),
    dt.getUTCMinutes(),
    dt.getUTCSeconds(),
    dt.getUTCMilliseconds()
  );
  return formatDate(utcDate, format);
}

export function calcClaimableMaxSeatCount(pass: TCartPass | TPass) {
  const { maxSeatCount, seatsClaimed } = pass;
  const maxCount = Math.max(maxSeatCount - seatsClaimed, 0);
  return maxCount;
}

export function isUserHasThePass(
  passesOnAccount: TPassOnAccount[],
  passId: number
) {
  return passesOnAccount.some((pass) => pass.passId === passId);
}

export function capitalizeString(str?: string) {
  if (!str) return "";
  str = str.toLowerCase();
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function isUserGroupLeader(
  groupDetails: TGlobalStore["groupDetails"],
  user: TGlobalStore["user"]
) {
  if (!groupDetails) return false;
  const leader = groupDetails?.groupMembers.find((member) => member.isLeader);
  return leader?.userId === user?.userId;
}

function insertChar(str: string, position: number, char: string) {
  return str.slice(0, position) + char + str.slice(position);
}

export function formatPhoneNumber(str: string) {
  let formatted = str;
  if (str.length == 3) formatted = str + "-";
  if (str.length == 7) formatted = str + "-";
  if (str.length == 4 && str[4] !== "-") formatted = insertChar(str, 3, "-");
  if (str.length == 8 && str[8] !== "-") formatted = insertChar(str, 7, "-");
  return formatted;
}

export function formatUSPhoneNumber(input?: string) {
  if (!input) return "";
  // Remove any non-digit characters from the input
  let digits = input.replace(/\D/g, "");

  // Ensure the number is no longer than 10 digits
  if (digits.length > 10) {
    digits = digits.slice(0, 10);
  }

  // Format the phone number
  let formatted = digits;
  if (digits.length > 3) {
    formatted = digits.slice(0, 3) + "-" + digits.slice(3);
  }
  if (digits.length > 6) {
    formatted = formatted.slice(0, 7) + "-" + formatted.slice(7);
  }

  return formatted;
}

export function formatUSPhoneNumberWithBracket(input?: string) {
  if (!input) return "";

  const formatted = `(${input.slice(0, 3)}) ${input.slice(
    3,
    6
  )} - ${input.slice(6)}`;

  return formatted;
}

export function openPdfInNewTab(pdfUrl: string) {
  const newWindow = window.open(pdfUrl, "_blank");
  if (newWindow) newWindow.opener = null;
}

export function crc32(str: string) {
  let crc = 0 ^ -1;
  for (let i = 0; i < str.length; i++) {
    crc = (crc >>> 8) ^ ((crc ^ str.charCodeAt(i)) & 0xff);
  }
  return (crc ^ -1) >>> 0;
}
